import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell } from "../components/AppShell";
import { CompanyLogo } from "../components/CompanyLogo";
import { FeedbackControl } from "../components/FeedbackControl";
import { Card, ErrorBox, FreshnessStamp, ProvenanceBadge, Select, Skeleton } from "../components/ui";
import {
  requestVarianceCommentary, useBalanceSheet, useFinanceLedger, useFinanceMutation, useFinanceOverview,
  type Ageing, type AgeingRow, type BalanceSheet, type CloseTask, type FinException, type FinanceCommentaryResponse, type FinanceOverview, type VarianceFact,
  useFinanceDocuments,
} from "../lib/api";
import { fmtNumber, fmtPct, fmtPeriod, sameLanguage } from "../lib/format";
import { markdownToHtml } from "../lib/markdown";

const mcdf = (v: number | null | undefined, lng: string, digits = 1) => (v == null ? "–" : `${fmtNumber(v / 1e6, lng, digits)} M CDF`);
const fmtDate = (s: string | null | undefined) => (s ? s.slice(0, 10) : "–");
const STATUS_PILL: Record<string, string> = {
  Draft: "bg-brand-grey-light text-brand-charcoal", Reviewed: "bg-blue-50 text-brand-primary", Approved: "bg-green-50 text-status-green", Rejected: "bg-red-50 text-status-red",
  Open: "bg-amber-50 text-status-amber", "In review": "bg-blue-50 text-brand-primary", Confirmed: "bg-red-50 text-status-red", Resolved: "bg-green-50 text-status-green", Dismissed: "bg-brand-grey-light text-brand-grey",
  Pending: "bg-brand-grey-light text-brand-charcoal", "In progress": "bg-blue-50 text-brand-primary", Done: "bg-green-50 text-status-green", "Signed off": "bg-green-100 text-status-green", Blocked: "bg-red-50 text-status-red",
};
const Pill = ({ value, label }: { value: string; label?: string }) => <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[value] ?? "bg-brand-grey-light text-brand-charcoal"}`}>{label ?? value}</span>;
const btn = "rounded border border-brand-grey-light px-2 py-0.5 text-xs font-semibold hover:bg-brand-grey-light/40 disabled:opacity-50";
const btnPrimary = "rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50";

/** Finance on the real ledgers (WP-08, FIN-02 … FIN-14): consolidated P&L through the group chart of accounts, ageing and
 * working capital, advisory controls, month-end close and a reviewable variance commentary — every figure from GL Entry. */
export default function Finance() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [period, setPeriod] = useState("");
  const q = useFinanceOverview(period || undefined);
  const d = q.data;
  const months = d?.months ?? [];
  return (
    <AppShell subtitle={t("tiles.finance.title")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mt-1 font-heading text-2xl font-semibold">{t("fin.title")}</h1>
          <p className="max-w-3xl text-sm text-brand-grey">{t("fin.subtitle")}</p>
        </div>
        <div className="flex items-end gap-3">
          {d && <FreshnessStamp lastUpdated={d.freshness.last_updated || null} refreshClass={d.freshness.class} />}
          <Select label={t("fin.period")} value={d?.period ?? period} onChange={setPeriod} options={months.map((m) => ({ value: m, label: fmtPeriod(m, lng) }))} />
        </div>
      </div>
      {q.isError && <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>}
      {q.isLoading && <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-6">{Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>}
      {d && (
        <>
          {!d.permissions.can_decide && <p className="mt-3 text-xs text-brand-grey">{t("fin.readOnly")}</p>}
          <KpiTiles d={d} lng={lng} />
          <PnlTable d={d} lng={lng} />
          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <CompanyMatrix d={d} lng={lng} />
            <CommentaryCard d={d} lng={lng} />
          </div>
          <BalanceSheetSection period={period} lng={lng} />
          <AgeingSection d={d} lng={lng} />
          <ControlsSection d={d} lng={lng} />
          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <CoaSection d={d} lng={lng} />
            <CloseSection d={d} lng={lng} />
          </div>
          <DocumentRegisters lng={lng} />
        </>
      )}
    </AppShell>
  );
}

function DocumentRegisters({ lng }: { lng: string }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState("purchase_order");
  // a document id or a party name, applied on Enter: sixty rows of the newest is the register, and the one document
  // somebody is asking about is usually older than that
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");
  const q = useFinanceDocuments(kind, search);
  const d = q.data;
  const pct = (v?: number) => (v === undefined || v === null ? "–" : `${fmtNumber(v, lng, 0)} %`);
  // each amount in the currency it was raised in: this register lists a USD purchase order beside a CDF one
  const amount = (v: number, cur?: string | null) => `${fmtNumber(v, lng, 0)}\u00a0${cur ?? ""}`.trim();
  return (
    <Card className="mt-6 overflow-hidden" data-fin-documents>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <h2 className="font-heading text-base font-semibold">{t("fin.docsTitle")}</h2>
          <p className="max-w-3xl text-xs text-brand-grey">{t("fin.docsHint")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <form onSubmit={(e) => { e.preventDefault(); setSearch(term.trim()); }} className="mr-2">
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={t("fin.docsSearch")}
              aria-label={t("fin.docsSearch")} data-fin-doc-search
              className="w-48 rounded border border-brand-grey-light px-2 py-1 text-[11px]" />
          </form>
          {(d?.registers ?? []).map((r: { kind: string; label: string }) => (
            <button key={r.kind} onClick={() => setKind(r.kind)} data-fin-doc-kind={r.kind}
              className={`rounded px-2 py-1 text-[11px] font-semibold ${kind === r.kind ? "bg-brand-primary text-white" : "bg-brand-grey-light/60 text-brand-grey"}`}>
              {t(`fin.docs.${r.kind}`)}
            </button>
          ))}
        </div>
      </div>
      {!d ? (
        <div className="px-4 pb-4"><Skeleton className="h-24" /></div>
      ) : (
        <>
          <div className="flex flex-wrap gap-5 border-y border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[12px]">
            <span><strong>{d.totals.count}</strong> {t(d.totals.count === 1 ? "fin.docsRow" : "fin.docsRows")}</span>
            <span>{t("fin.docsTotal")} <strong className="tabular-nums">{amount(d.totals.total, d.base_currency)}</strong></span>
            {d.currencies.some((c) => c !== d.base_currency) && (
              <span className="text-brand-grey">{t("fin.docsConverted", { list: d.currencies.join(", "), base: d.base_currency })}</span>
            )}
            {d.totals.outstanding > 0 && (
              <span className="text-status-amber">{t("fin.docsOutstanding")}{" "}
                <strong className="tabular-nums">{amount(d.totals.outstanding, d.base_currency)}</strong> ({d.totals.open})</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("fin.docsId")}</th>
                  <th className="px-4 py-2">{t("fin.docsDate")}</th>
                  <th className="px-4 py-2">{t("fin.docsParty")}</th>
                  <th className="px-2 py-2 text-right">{t("fin.docsAmount")}</th>
                  <th className="px-4 py-2">{t("ops.status")}</th>
                  {d.columns.includes("per_received") && <th className="px-2 py-2 text-right">{t("fin.docsReceived")}</th>}
                  {d.columns.includes("per_delivered") && <th className="px-2 py-2 text-right">{t("fin.docsDelivered")}</th>}
                  {d.columns.includes("per_billed") && <th className="px-2 py-2 text-right">{t("fin.docsBilled")}</th>}
                  {d.columns.includes("outstanding") && <th className="px-2 py-2 text-right">{t("fin.docsOwed")}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-grey-light">
                {d.rows.map((r) => (
                  <tr key={r.name} data-fin-doc={r.name}>
                    <td className="px-4 py-2 font-mono text-[12px]">{r.name}</td>
                    <td className="px-4 py-2 text-[12px] text-brand-grey">{r.date}</td>
                    <td className="px-4 py-2">{r.party_name || r.party}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{amount(r.total ?? 0, r.currency)}</td>
                    <td className="px-4 py-2 text-[12px]">{r.status}</td>
                    {d.columns.includes("per_received") && <td className="px-2 py-2 text-right tabular-nums">{pct(r.per_received)}</td>}
                    {d.columns.includes("per_delivered") && <td className="px-2 py-2 text-right tabular-nums">{pct(r.per_delivered)}</td>}
                    {d.columns.includes("per_billed") && <td className="px-2 py-2 text-right tabular-nums">{pct(r.per_billed)}</td>}
                    {d.columns.includes("outstanding") && (
                      <td className={`px-2 py-2 text-right tabular-nums ${(r.outstanding ?? 0) > 0 ? "text-status-amber" : "text-brand-grey"}`}>
                        {amount(r.outstanding ?? 0, r.currency)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

function lineValue(d: FinanceOverview, line: string, month: string): number {
  const ln = d.pnl.lines.find((x) => x.line === line) ?? d.pnl.subtotals.find((x) => x.line === line);
  return ln?.values[month] ?? 0;
}

function KpiTiles({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const P = d.period;
  const prevFact = (label: string) => d.variance.facts.find((f) => f.kind === "vs_previous_month" && f.label === label);
  const margin = d.variance.facts.find((f) => f.kind === "margin_points");
  const openEx = d.exceptions.filter((e) => e.status === "Open");
  const delta = (f: VarianceFact | undefined) => f && f.delta_pct != null ? <span className={`text-[11px] ${f.delta >= 0 ? "text-status-green" : "text-status-red"}`}>{fmtPct(f.delta_pct, lng)} {t("fin.kpi.vsPrev")}</span> : null;
  const tiles: { key: string; value: string; sub?: React.ReactNode; tone?: string }[] = [
    { key: "revenue", value: mcdf(lineValue(d, "Revenue", P), lng), sub: delta(prevFact("Revenue")) },
    { key: "grossMargin", value: d.variance.gross_margin_pct == null ? "–" : `${fmtNumber(d.variance.gross_margin_pct, lng, 1)} %`, sub: margin ? <span className={`text-[11px] ${margin.delta >= 0 ? "text-status-green" : "text-status-red"}`}>{margin.delta > 0 ? "+" : ""}{fmtNumber(margin.delta, lng, 1)} {t("fin.kpi.pp")} {t("fin.kpi.vsPrev")}</span> : undefined },
    { key: "ebitda", value: mcdf(lineValue(d, "EBITDA", P), lng), sub: delta(prevFact("EBITDA")), tone: lineValue(d, "EBITDA", P) < 0 ? "text-status-red" : "" },
    { key: "operating", value: mcdf(lineValue(d, "Operating result", P), lng), sub: delta(prevFact("Operating result")), tone: lineValue(d, "Operating result", P) < 0 ? "text-status-red" : "" },
    { key: "cash", value: mcdf(d.working_capital.cash_and_bank, lng, 0), sub: <span className="text-[11px] text-brand-grey">{t("fin.asOf")} {fmtDate(d.as_of)}</span> },
    { key: "nwc", value: mcdf(d.working_capital.net_working_capital, lng, 0), sub: <span className="text-[11px] text-brand-grey">{t("fin.ageing.dso")} {d.working_capital.dso_days ?? "–"} · {t("fin.ageing.dpo")} {d.working_capital.dpo_days ?? "–"} {t("fin.ageing.days")}</span> },
    { key: "arOverdue", value: mcdf(d.ageing.receivables.total.overdue, lng, 0), sub: <span className="text-[11px] text-brand-grey">{fmtNumber(d.ageing.receivables.total.overdue_pct, lng, 0)} % {t("fin.kpi.ofTotal")}</span>, tone: d.ageing.receivables.total.overdue_pct > 50 ? "text-status-red" : "" },
    { key: "apOverdue", value: mcdf(d.ageing.payables.total.overdue, lng, 0), sub: <span className="text-[11px] text-brand-grey">{fmtNumber(d.ageing.payables.total.overdue_pct, lng, 0)} % {t("fin.kpi.ofTotal")}</span> },
    { key: "unmapped", value: mcdf(d.coa.unmapped_balance, lng, 2), sub: <span className="text-[11px] text-brand-grey">{d.coa.unmapped_with_balance} {t("fin.coa.unmapped").toLowerCase()}</span>, tone: d.coa.unmapped_with_balance ? "text-status-amber" : "text-status-green" },
    { key: "exceptions", value: String(openEx.length), sub: <span className="text-[11px] text-brand-grey">{mcdf(openEx.reduce((s, e) => s + e.amount, 0), lng, 0)}</span>, tone: openEx.length ? "text-status-amber" : "text-status-green" },
    { key: "duplicates", value: String(d.duplicates_count), sub: <span className="text-[11px] text-brand-grey">{mcdf(d.duplicates_payable, lng, 0)} {t("fin.kpi.stillPayable")}</span>, tone: d.duplicates_count ? "text-status-red" : "text-status-green" },
    { key: "close", value: `${d.close.progress.pct} %`, sub: <span className="text-[11px] text-brand-grey">{d.close.progress.done}/{d.close.progress.total} · {d.close.progress.overdue} {t("fin.close.overdue")}</span>, tone: d.close.progress.overdue ? "text-status-amber" : "" },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tl) => (
        <Card key={tl.key} className="px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`fin.kpi.${tl.key}`)}</div>
          <div className={`font-heading text-xl font-semibold ${tl.tone ?? ""}`}>{tl.value}</div>
          <div className="min-h-[16px]">{tl.sub}</div>
        </Card>
      ))}
    </div>
  );
}

function PnlTable({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const [openLine, setOpenLine] = useState<string | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [ledger, setLedger] = useState<{ account: string; month: string } | null>(null);
  const P = d.period;
  const months = d.pnl.months;
  const subtotalAfter: Record<string, string> = { "Cost of sales": "Gross margin", "Other operating expenses": "EBITDA", Depreciation: "Operating result", "Finance costs": "Net result" };
  const label = (line: string) => t(`fin.lines.${line}`, line);
  const cell = (v: number, strong = false) => <td className={`px-3 py-1.5 text-right tabular-nums ${strong ? "font-semibold" : ""} ${v < 0 ? "text-status-red" : ""}`}>{fmtNumber(v / 1e6, lng, 1)}</td>;
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
        <div>
          <h2 className="font-heading text-base font-semibold">{t("fin.pnl.title")} <span className="ml-2 text-xs font-normal text-brand-grey">M CDF · {months.length} {t("fin.months")}</span></h2>
          <p className="text-xs text-brand-grey">{t("fin.pnl.hint")}</p>
        </div>
        <ProvenanceBadge source="rule" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
            <tr><th className="px-3 py-2">{t("fin.pnl.line")}</th>{months.map((m) => <th key={m} className={`px-3 py-2 text-right ${m === P ? "text-brand-primary" : ""}`}>{fmtPeriod(m, lng)}</th>)}</tr>
          </thead>
          <tbody>
            {d.pnl.lines.map((ln) => {
              const groups = d.pnl.groups.filter((g) => g.line === ln.line);
              const open = openLine === ln.line;
              const hasElim = months.some((m) => (ln.elimination?.[m] ?? 0) !== 0);
              return [
                <tr key={ln.line} className={`border-t border-brand-grey-light ${groups.length ? "cursor-pointer hover:bg-[#F6F8FA]" : ""}`} onClick={() => { if (groups.length) { setOpenLine(open ? null : ln.line); setOpenGroup(null); } }}>
                  <td className="px-3 py-1.5 font-medium">{groups.length ? <span className="mr-1 text-brand-grey">{open ? "▾" : "▸"}</span> : <span className="mr-1 text-brand-grey-light">·</span>}{label(ln.line)}</td>
                  {months.map((m) => cell(ln.values[m] ?? 0))}
                </tr>,
                ...(open && hasElim ? [
                  <tr key={`${ln.line}-gross`} className="bg-[#FBFCFD] text-xs text-brand-grey"><td className="px-3 py-1 pl-8">{t("fin.pnl.gross")}</td>{months.map((m) => <td key={m} className="px-3 py-1 text-right tabular-nums">{fmtNumber((ln.gross?.[m] ?? 0) / 1e6, lng, 1)}</td>)}</tr>,
                  <tr key={`${ln.line}-elim`} className="bg-[#FBFCFD] text-xs text-brand-grey"><td className="px-3 py-1 pl-8">{t("fin.pnl.elimination")}</td>{months.map((m) => <td key={m} className="px-3 py-1 text-right tabular-nums">{fmtNumber((ln.elimination?.[m] ?? 0) / 1e6, lng, 1)}</td>)}</tr>,
                ] : []),
                ...(open ? groups.flatMap((g) => {
                  const gOpen = openGroup === g.group_account;
                  return [
                    <tr key={g.group_account} className="cursor-pointer bg-[#FBFCFD] text-xs hover:bg-[#F6F8FA]" onClick={() => setOpenGroup(gOpen ? null : g.group_account)}>
                      <td className="px-3 py-1 pl-8"><span className="mr-1 text-brand-grey">{gOpen ? "▾" : "▸"}</span><span className="font-mono text-brand-grey">{g.group_account}</span> {g.group_name} <span className="text-brand-grey">· {g.accounts.length} {t("fin.pnl.accounts")}</span></td>
                      {months.map((m) => <td key={m} className="px-3 py-1 text-right tabular-nums">{fmtNumber((g.values[m] ?? 0) / 1e6, lng, 1)}</td>)}
                    </tr>,
                    ...(gOpen ? g.accounts.map((a) => (
                      <tr key={a.account} className="cursor-pointer text-xs text-brand-charcoal hover:bg-blue-50/40" onClick={() => setLedger({ account: a.account, month: P })}>
                        <td className="px-3 py-1 pl-14"><CompanyLogo company={a.company} size={12} className="mr-1.5 align-middle" />{a.account}</td>
                        {months.map((m) => <td key={m} className="px-3 py-1 text-right tabular-nums">{fmtNumber((a.values[m] ?? 0) / 1e6, lng, 1)}</td>)}
                      </tr>
                    )) : []),
                  ];
                }) : []),
                ...(subtotalAfter[ln.line] ? [(() => {
                  const s = d.pnl.subtotals.find((x) => x.line === subtotalAfter[ln.line]);
                  return s ? <tr key={s.line} className="border-t-2 border-brand-grey-light bg-[#F6F8FA] font-semibold"><td className="px-3 py-1.5">{label(s.line)}</td>{months.map((m) => cell(s.values[m] ?? 0, true))}</tr> : null;
                })()] : []),
              ];
            })}
            <tr className="border-t border-brand-grey-light text-xs text-brand-grey" title={t("fin.pnl.eliminationHint", { pairs: d.eliminations.pairs })}>
              <td className="px-3 py-1.5">{label(d.pnl.elimination.line)} <span className="text-brand-grey-light">ⓘ</span></td>{months.map((m) => <td key={m} className="px-3 py-1.5 text-right tabular-nums">{fmtNumber((d.pnl.elimination.values[m] ?? 0) / 1e6, lng, 1)}</td>)}
            </tr>
            <tr className={`border-t border-brand-grey-light text-xs ${d.pnl.unmapped_accounts.length ? "text-status-amber" : "text-brand-grey"}`} title={t("fin.pnl.unmappedHint")}>
              <td className="px-3 py-1.5">{label(d.pnl.unmapped.line)} · {d.pnl.unmapped_accounts.length} <span className="text-brand-grey-light">ⓘ</span></td>{months.map((m) => <td key={m} className="px-3 py-1.5 text-right tabular-nums">{fmtNumber((d.pnl.unmapped.values[m] ?? 0) / 1e6, lng, 2)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {ledger && <LedgerPanel account={ledger.account} month={ledger.month} onClose={() => setLedger(null)} lng={lng} />}
    </Card>
  );
}

function LedgerPanel({ account, month, onClose, lng }: { account: string; month: string; onClose: () => void; lng: string }) {
  const { t } = useTranslation();
  const q = useFinanceLedger(account, month);
  return (
    <div className="border-t border-brand-grey-light bg-[#FBFCFD] px-4 py-3 text-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-semibold">{t("fin.pnl.ledger")} · {account} · {fmtPeriod(month, lng)}</h3>
        <button onClick={onClose} className={btn}>{t("fin.pnl.close")}</button>
      </div>
      {q.isLoading && <Skeleton className="mt-2 h-16" />}
      {q.isError && <ErrorBox error={q.error} />}
      {q.data && (q.data.rows.length === 0 ? <p className="mt-2 text-xs text-brand-grey">{t("fin.pnl.noLedger")}</p> : (
        <table className="mt-2 w-full text-xs">
          <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{t("fin.pnl.date")}</th><th>{t("fin.pnl.voucher")}</th><th>{t("fin.pnl.party")}</th><th className="text-right">{t("fin.pnl.debit")}</th><th className="text-right">{t("fin.pnl.credit")}</th></tr></thead>
          <tbody>
            {q.data.rows.map((r) => (
              <tr key={r.name} className="border-t border-brand-grey-light/60">
                <td className="py-1">{fmtDate(r.posting_date)}</td>
                <td><span>{r.voucher_type} {r.voucher_no}</span></td>
                <td className="text-brand-grey">{r.party || r.against || ""}</td>
                <td className="text-right tabular-nums">{r.debit ? fmtNumber(r.debit, lng, 0) : ""}</td>
                <td className="text-right tabular-nums">{r.credit ? fmtNumber(r.credit, lng, 0) : ""}</td>
              </tr>
            ))}
            <tr className="border-t border-brand-grey-light font-semibold"><td colSpan={3} className="py-1">{q.data.count}</td><td className="text-right tabular-nums">{fmtNumber(q.data.debit, lng, 0)}</td><td className="text-right tabular-nums">{fmtNumber(q.data.credit, lng, 0)}</td></tr>
          </tbody>
        </table>
      ))}
    </div>
  );
}

function CompanyMatrix({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const P = d.period;
  const rows = useMemo(() => [...d.pnl.companies].sort((a, b) => (b.revenue[P] ?? 0) - (a.revenue[P] ?? 0)), [d.pnl.companies, P]);
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-brand-grey-light px-4 py-3">
        <h2 className="font-heading text-base font-semibold">{t("fin.companies.title")} · {fmtPeriod(P, lng)}</h2>
        <p className="text-xs text-brand-grey">{t("fin.companies.hint")}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
            <tr><th className="px-3 py-2">{t("exec.division")}</th><th className="px-3 py-2 text-right">{t("fin.companies.revenue")}</th><th className="px-3 py-2 text-right">{t("fin.companies.gm")}</th><th className="px-3 py-2 text-right">{t("fin.companies.opex")}</th><th className="px-3 py-2 text-right">{t("fin.companies.result")}</th><th className="px-3 py-2 text-right">{t("fin.companies.unmapped")}</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.company} className="border-t border-brand-grey-light">
                <td className="px-3 py-1.5 font-semibold"><CompanyLogo company={c.company} size={16} showName /></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtNumber((c.revenue[P] ?? 0) / 1e6, lng, 1)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${(c.gross_margin_pct[P] ?? 0) < 0 ? "text-status-red" : ""}`}>{c.gross_margin_pct[P] == null ? "–" : `${fmtNumber(c.gross_margin_pct[P], lng, 1)} %`}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtNumber((c.opex[P] ?? 0) / 1e6, lng, 1)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${(c.operating_result[P] ?? 0) < 0 ? "text-status-red" : "text-status-green"}`}>{fmtNumber((c.operating_result[P] ?? 0) / 1e6, lng, 1)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-brand-grey">{c.unmapped[P] ? fmtNumber((c.unmapped[P] ?? 0) / 1e6, lng, 2) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 text-[11px] text-brand-grey">M CDF</p>
    </Card>
  );
}

function CommentaryCard({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mutation = useFinanceMutation();
  const [drafting, setDrafting] = useState(false);
  const [aiResult, setAiResult] = useState<FinanceCommentaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const c = d.commentary;
  // a stored rule draft is regenerated in the person's language; AI and human texts are shown as written (with their language)
  const storedText = c && c.source !== "deterministic" && sameLanguage(c.language, lng) ? c.summary : "";
  const shownText = aiResult?.text || storedText || (lng.startsWith("en") ? d.rule_commentary_en : d.rule_commentary);
  const storedLang = !aiResult && storedText && c?.language && !lng.startsWith(c.language) ? c.language : null;
  const shownSource: "rule" | "ai" | "human" = aiResult ? (aiResult.source === "ai" ? "ai" : "rule") : c ? (c.source === "ai" ? "ai" : c.source === "human" ? "human" : "rule") : "rule";
  const interactionId = aiResult?.interaction_id || c?.ai_interaction_id || null;
  const onDraft = async () => {
    setDrafting(true); setError(null);
    try {
      const r = await requestVarianceCommentary(d.period, lng);
      setAiResult(r);
      void qc.invalidateQueries({ queryKey: ["finance_overview"] });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setDrafting(false); }
  };
  const factLabel = (f: VarianceFact) => t(`fin.lines.${f.label}`, f.label);
  return (
    <Card className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-heading text-base font-semibold"><span className="text-violet-600">✦</span> {t("fin.commentary.title")} · {fmtPeriod(d.period, lng)} <ProvenanceBadge source={shownSource} />{c && !aiResult && <Pill value={c.status} label={t(`fin.commentary.status.${c.status}`, c.status)} />}{(aiResult?.model || c?.model) && <span className="text-[11px] font-normal text-brand-grey">{aiResult?.model || c?.model}</span>}</div>
        {d.permissions.can_draft_commentary && <button disabled={drafting} onClick={() => void onDraft()} className="rounded bg-violet-600 px-3 py-1 text-sm font-semibold text-white disabled:opacity-50">{drafting ? t("fin.commentary.drafting") : t("fin.commentary.draft")}</button>}
      </div>
      <p className="mt-1 text-xs text-brand-grey">{t("fin.commentary.hint")} · {t("fin.commentary.threshold", { pct: d.variance.threshold_pct, amount: mcdf(d.variance.materiality, lng, 0) })}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3 text-xs">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.commentary.facts")}</div>
          {d.variance.significant.length === 0 ? <p className="text-brand-grey">{t("fin.commentary.noFacts")}</p> : (
            <ul className="mt-1 space-y-0.5">{d.variance.significant.filter((f) => f.kind === "vs_previous_month").map((f) => <li key={f.label} className="flex justify-between gap-2"><span>{factLabel(f)}</span><span className={`tabular-nums ${f.delta >= 0 ? "text-status-green" : "text-status-red"}`}>{fmtPct(f.delta_pct, lng)}</span></li>)}</ul>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.commentary.drivers")}</div>
          <ul className="mt-1 space-y-0.5">{d.variance.expense_drivers.slice(0, 4).map((f) => <li key={f.group_account} className="flex justify-between gap-2"><span>{f.label}</span><span className="tabular-nums">{fmtPct(f.delta_pct, lng)} · {mcdf(f.delta, lng)}</span></li>)}</ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.commentary.movers")}</div>
          <ul className="mt-1 space-y-0.5">{d.variance.company_movers.slice(0, 4).map((f, i) => <li key={`${f.company}-${i}`} className="flex justify-between gap-2"><span><CompanyLogo company={f.company} size={12} className="mr-1 align-middle" />{f.company} · {t(`fin.companies.${f.label === "revenue" ? "revenue" : "result"}`)}</span><span className={`tabular-nums ${f.delta >= 0 ? "text-status-green" : "text-status-red"}`}>{fmtPct(f.delta_pct, lng)}</span></li>)}</ul>
        </div>
      </div>
      {error && <div className="mt-2 rounded bg-red-50 px-3 py-2 text-xs text-status-red">{error}</div>}
      <article className="prose-pack mt-3 rounded border border-brand-grey-light/70 bg-[#FBFCFD] px-3 py-2 text-sm leading-relaxed text-brand-charcoal" dangerouslySetInnerHTML={{ __html: markdownToHtml(shownText || t("fin.commentary.noCommentary")) }} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-brand-grey">
        <span>{c?.reviewed_by ? `${t(`fin.commentary.status.${c.status}`, c.status)} ${t("fin.commentary.reviewedBy")} ${c.reviewed_by} · ${fmtDate(c.reviewed_on)}` : shownSource === "rule" ? t("fin.commentary.ruleDraft") : ""}{storedLang && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900">{t("lang.written", { lang: t(`lang.${storedLang}`) })}</span>}</span>
        {c && d.permissions.can_review_commentary && (
          <div className="flex gap-1">
            {c.status !== "Reviewed" && c.status !== "Approved" && <button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "review_commentary", params: { name: c.name, status: "Reviewed" } })} className={btn}>{t("fin.commentary.review")}</button>}
            {c.status !== "Approved" && <button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "review_commentary", params: { name: c.name, status: "Approved", feedback: "Correct" } })} className={`${btn} border-status-green text-status-green`}>{t("fin.commentary.approve")}</button>}
            {c.status !== "Rejected" && <button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "review_commentary", params: { name: c.name, status: "Rejected", feedback: "Needs review" } })} className={`${btn} border-status-red text-status-red`}>{t("fin.commentary.reject")}</button>}
          </div>
        )}
      </div>
      {shownSource === "ai" && <FeedbackControl interactionId={interactionId} className="mt-2" />}
    </Card>
  );
}

function AgeingTable({ a, title, lng }: { a: Ageing; title: string; lng: string }) {
  const { t } = useTranslation();
  const cols: (keyof AgeingRow)[] = ["not_due", "d1_30", "d31_60", "d61_90", "d90_plus"];
  const heads = ["notDue", "d1", "d31", "d61", "d90"];
  return (
    <div className="overflow-x-auto">
      <h3 className="px-4 pt-3 font-heading text-sm font-semibold">{title} <span className="ml-1 text-xs font-normal text-brand-grey">M CDF · {t("fin.asOf")} {fmtDate(a.as_of)}</span></h3>
      <table className="mt-1 w-full text-xs">
        <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="px-4 py-1">{t("fin.ageing.company")}</th><th className="px-2 py-1 text-right">{t("fin.ageing.total")}</th>{heads.map((h) => <th key={h} className="px-2 py-1 text-right">{t(`fin.ageing.${h}`)}</th>)}<th className="px-2 py-1 text-right">{t("fin.ageing.overdue")}</th></tr></thead>
        <tbody>
          {a.companies.map((r) => (
            <tr key={r.company} className="border-t border-brand-grey-light/60">
              <td className="px-4 py-1"><CompanyLogo company={r.company} size={12} className="mr-1 align-middle" />{r.company}</td>
              <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmtNumber(r.total / 1e6, lng, 1)}</td>
              {cols.map((k) => <td key={k} className={`px-2 py-1 text-right tabular-nums ${k === "d90_plus" && r[k] ? "text-status-red" : ""}`}>{r[k] ? fmtNumber((r[k] as number) / 1e6, lng, 1) : ""}</td>)}
              <td className={`px-2 py-1 text-right tabular-nums ${r.overdue_pct > 50 ? "text-status-red" : ""}`}>{fmtNumber(r.overdue_pct, lng, 0)} %</td>
            </tr>
          ))}
          <tr className="border-t border-brand-grey-light bg-[#F6F8FA] font-semibold">
            <td className="px-4 py-1">{t("fin.ageing.total")}</td><td className="px-2 py-1 text-right tabular-nums">{fmtNumber(a.total.total / 1e6, lng, 1)}</td>
            {cols.map((k) => <td key={k} className="px-2 py-1 text-right tabular-nums">{fmtNumber((a.total[k as keyof typeof a.total] as number) / 1e6, lng, 1)}</td>)}
            <td className="px-2 py-1 text-right tabular-nums">{fmtNumber(a.total.overdue_pct, lng, 0)} %</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Consolidated balance sheet (FIN-06): the position at a date, through the same approved group mapping as the P&L.
 * The difference between the two sides is shown rather than hidden — during the year it is the result that has not
 * been closed to equity, and only what is left after that ("unexplained") is worth alarming on. */
function BalanceSheetSection({ period, lng }: { period: string; lng: string }) {
  const { t } = useTranslation();
  const q = useBalanceSheet(period || undefined);
  const [open, setOpen] = useState<string | null>(null);
  if (q.isLoading) return <Card className="mt-6 p-4"><Skeleton className="h-40" /></Card>;
  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  const bs = q.data as BalanceSheet | undefined;
  if (!bs) return null;
  const side = (s: BalanceSheet["lines"][number]["side"]) => bs.lines.filter((l) => l.side === s && l.amount !== 0);
  const assets = side("asset");
  const liabs = [...side("liability"), ...side("equity")];
  const groupsFor = (line: string) => bs.groups.filter((g) => g.line === line);
  const row = (l: BalanceSheet["lines"][number]) => (
    <div key={l.line}>
      <button onClick={() => setOpen(open === l.line ? null : l.line)} className="flex w-full items-center justify-between border-t border-brand-grey-light/70 py-1.5 text-left hover:bg-[#F6F8FA]">
        <span>{lng.startsWith("en") ? l.line : l.line_fr}</span>
        <span className="tabular-nums font-medium">{mcdf(l.amount, lng)}</span>
      </button>
      {open === l.line && (
        <ul className="bg-[#F6F8FA] px-3 py-1 text-[11px] text-brand-grey">
          {groupsFor(l.line).map((g) => (
            <li key={g.group_account} className="flex justify-between py-0.5"><span>{g.group_name}</span><span className="tabular-nums">{mcdf(g.balance, lng)}</span></li>
          ))}
          {groupsFor(l.line).length === 0 && <li className="py-0.5">{t("fin.bs.noDetail")}</li>}
        </ul>
      )}
    </div>
  );
  return (
    <Card className="mt-6 p-4" data-fin-bs>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base font-semibold">{t("fin.bs.title")}
          <span className="ml-2 text-xs font-normal text-brand-grey">M CDF · {t("fin.asOf")} {fmtDate(bs.as_of)}</span></h2>
        <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${bs.balanced ? "bg-green-50 text-status-green" : "bg-red-50 text-status-red"}`} data-fin-bs-balanced>
          {bs.balanced ? t("fin.bs.balanced") : t("fin.bs.unbalanced", { amount: mcdf(bs.unexplained ?? bs.difference, lng) })}
        </span>
      </div>
      <div className="mt-3 grid gap-6 text-xs md:grid-cols-2">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("fin.bs.assets")}</h3>
          {assets.map(row)}
          <div className="flex justify-between border-t-2 border-brand-primary/40 py-1.5 font-heading font-semibold"><span>{t("fin.bs.totalAssets")}</span><span className="tabular-nums">{mcdf(bs.assets, lng)}</span></div>
        </div>
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("fin.bs.liabilities")}</h3>
          {liabs.map(row)}
          <div className="flex justify-between border-t-2 border-brand-primary/40 py-1.5 font-heading font-semibold"><span>{t("fin.bs.totalLiabilities")}</span><span className="tabular-nums">{mcdf(bs.liabilities_equity, lng)}</span></div>
        </div>
      </div>
      <div className="mt-3 space-y-0.5 border-t border-brand-grey-light pt-2 text-[11px] text-brand-grey">
        {bs.result_not_closed != null && (
          <div className="flex justify-between"><span>{t("fin.bs.resultNotClosed")}</span><span className="tabular-nums">{mcdf(bs.result_not_closed, lng)}</span></div>
        )}
        {bs.unmapped !== 0 && (
          <div className="flex justify-between text-status-amber"><span>{t("fin.bs.unmapped", { count: bs.unmapped_accounts.length })}</span><span className="tabular-nums">{mcdf(bs.unmapped, lng)}</span></div>
        )}
        <p>{t("fin.bs.hint")}</p>
      </div>
    </Card>
  );
}

function AgeingSection({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const wc = d.working_capital;
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="border-b border-brand-grey-light px-4 py-3"><h2 className="font-heading text-base font-semibold">{t("fin.ageing.title")}</h2></div>
      <div className="grid gap-4 xl:grid-cols-2">
        <AgeingTable a={d.ageing.receivables} title={t("fin.ageing.receivables")} lng={lng} />
        <AgeingTable a={d.ageing.payables} title={t("fin.ageing.payables")} lng={lng} />
      </div>
      <div className="grid gap-4 border-t border-brand-grey-light px-4 py-3 text-xs md:grid-cols-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.ageing.topParties")}</div>
          <ul className="mt-1 space-y-0.5">{d.ageing.receivables.top_parties.filter((p) => p.overdue > 0).slice(0, 6).map((p) => <li key={`${p.company}-${p.party}`} className="flex justify-between gap-2"><span><CompanyLogo company={p.company} size={12} className="mr-1 align-middle" />{p.party}</span><span className="tabular-nums text-status-red">{mcdf(p.overdue, lng, 0)} · {p.oldest_days} {t("fin.ageing.days")}</span></li>)}</ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.kpi.nwc")}</div>
          <ul className="mt-1 space-y-0.5">
            <li className="flex justify-between"><span>{t("fin.ageing.receivablesTotal")}</span><span className="tabular-nums">{mcdf(wc.receivables, lng, 0)}</span></li>
            <li className="flex justify-between"><span>{t("fin.ageing.inventory")}</span><span className="tabular-nums">{mcdf(wc.inventory, lng, 0)}</span></li>
            <li className="flex justify-between"><span>{t("fin.ageing.payablesTotal")}</span><span className="tabular-nums">− {mcdf(wc.payables, lng, 0)}</span></li>
            <li className="flex justify-between border-t border-brand-grey-light pt-0.5 font-semibold"><span>{t("fin.kpi.nwc")}</span><span className="tabular-nums">{mcdf(wc.net_working_capital, lng, 0)}</span></li>
            <li className="flex justify-between text-brand-grey"><span>{t("fin.ageing.dso")} / {t("fin.ageing.dpo")} / {t("fin.ageing.dio")}</span><span className="tabular-nums">{wc.dso_days ?? "–"} / {wc.dpo_days ?? "–"} / {wc.dio_days ?? "–"} {t("fin.ageing.days")}</span></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("fin.ageing.cashByCompany")}</div>
          <ul className="mt-1 space-y-0.5">{[...d.cash].sort((a, b) => b.cash_and_bank - a.cash_and_bank).slice(0, 6).map((c) => <li key={c.company} className="flex justify-between gap-2"><span><CompanyLogo company={c.company} size={12} className="mr-1 align-middle" />{c.company}</span><span className="tabular-nums">{mcdf(c.cash_and_bank, lng, 0)}</span></li>)}</ul>
        </div>
      </div>
    </Card>
  );
}

function ControlsSection({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const mutation = useFinanceMutation();
  const [rerunning, setRerunning] = useState(false);
  const exceptions = d.exceptions.filter((e) => e.exception_type !== "Recurring vendor");
  const decide = (e: FinException, status: string) => mutation.mutate({ method: "decide_exception", params: { name: e.name, status } });
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
        <div><h2 className="font-heading text-base font-semibold">{t("fin.controls.title")}</h2><p className="text-xs text-brand-grey">{t("fin.controls.hint")}</p></div>
        <div className="flex items-center gap-2"><ProvenanceBadge source="rule" />{d.permissions.can_decide && <button disabled={rerunning} onClick={() => { setRerunning(true); mutation.mutate({ method: "run_controls", params: { period: d.period } }, { onSettled: () => setRerunning(false) }); }} className={btn}>{rerunning ? t("fin.controls.rerunning") : t("fin.controls.rerun")}</button>}</div>
      </div>
      <div className="px-4 py-3">
        <h3 className="font-heading text-sm font-semibold">{t("fin.controls.duplicates")} · {d.duplicates_count}</h3>
        {d.duplicates.length === 0 ? <p className="text-xs text-brand-grey">{t("fin.controls.none")}</p> : (
          <table className="mt-1 w-full text-xs">
            <tbody>
              {d.duplicates.map((x) => (
                <tr key={`${x.invoice}-${x.duplicate_of}`} className="border-t border-brand-grey-light/60">
                  <td className="py-1.5"><CompanyLogo company={x.company} size={12} className="mr-1 align-middle" />{x.supplier}</td>
                  <td className="py-1.5"><span>{x.invoice}</span> <span className="text-brand-grey">{x.bill_no}</span> ↔ <span>{x.duplicate_of}</span> <span className="text-brand-grey">{x.other_bill_no}</span></td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{fmtNumber(x.amount, lng, 0)} {x.currency}</td>
                  <td className="py-1.5">{x.reasons.map((r) => <span key={r} className="mr-1 rounded border border-status-amber/40 bg-amber-50 px-1.5 py-0.5 text-[10px] text-status-amber">{t(`fin.controls.reasons.${r}`, r)}</span>)}{x.days_apart != null && <span className="text-brand-grey">{x.days_apart} {t("fin.controls.daysApart")}</span>}</td>
                  <td className="py-1.5 text-right"><span className="font-semibold">{x.confidence} %</span> <span className="text-brand-grey">{t("fin.controls.confidence")}</span>{x.still_payable && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-status-red">{t("fin.controls.stillPayable")}</span>}{x.paid_twice && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-status-red">{t("fin.controls.paidTwice")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="border-t border-brand-grey-light px-4 py-3">
        <h3 className="font-heading text-sm font-semibold">{t("fin.controls.exceptions")} · {exceptions.length}</h3>
        {exceptions.length === 0 ? <p className="text-xs text-brand-grey">{t("fin.controls.none")}</p> : (
          <div className="overflow-x-auto">
            <table className="mt-1 w-full text-xs">
              <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{t("fin.controls.type")}</th><th>{t("fin.controls.reason")}</th><th>{t("exec.division")}</th><th className="text-right">{t("fin.controls.amount")}</th><th className="text-right">{t("fin.controls.difference")}</th><th>{t("fin.controls.owner")}</th><th>{t("fin.controls.due")}</th><th>{t("fin.controls.status")}</th><th /></tr></thead>
              <tbody>
                {exceptions.map((e) => (
                  <tr key={e.name} className="border-t border-brand-grey-light/60">
                    <td className="py-1.5"><Pill value={e.exception_type} label={t(`fin.controls.types.${e.exception_type}`, e.exception_type)} /></td>
                    <td className="py-1.5 max-w-md"><div className="font-medium">{e.title}</div><div className="text-brand-grey">{e.reason}{e.reference_name && <> · <span>{e.reference_name}</span></>}{e.related_name && <> ↔ <span>{e.related_name}</span></>}</div></td>
                    <td className="py-1.5"><CompanyLogo company={e.company} size={12} className="mr-1 align-middle" />{e.company}</td>
                    <td className="py-1.5 text-right tabular-nums">{fmtNumber(e.amount, lng, 0)} {e.currency}</td>
                    <td className="py-1.5 text-right tabular-nums">{e.difference ? fmtNumber(e.difference, lng, 0) : ""}</td>
                    <td className="py-1.5 text-brand-grey">{e.owner_user?.split("@")[0]}</td>
                    <td className={`py-1.5 ${e.due_date && e.due_date < d.as_of.slice(0, 10) && e.status === "Open" ? "text-status-red" : "text-brand-grey"}`}>{fmtDate(e.due_date)}</td>
                    <td className="py-1.5"><Pill value={e.status} label={t(`fin.controls.statuses.${e.status}`, e.status)} /></td>
                    <td className="py-1.5 text-right whitespace-nowrap">{d.permissions.can_decide && e.status !== "Resolved" && e.status !== "Dismissed" && (
                      <>{e.status === "Open" && <button disabled={mutation.isPending} onClick={() => decide(e, "Confirmed")} className={`${btn} mr-1 border-status-red text-status-red`}>{t("fin.controls.confirm")}</button>}
                        <button disabled={mutation.isPending} onClick={() => decide(e, "Resolved")} className={`${btn} mr-1 border-status-green text-status-green`}>{t("fin.controls.resolve")}</button>
                        <button disabled={mutation.isPending} onClick={() => decide(e, "Dismissed")} className={btn}>{t("fin.controls.dismiss")}</button></>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="border-t border-brand-grey-light px-4 py-3">
        <h3 className="font-heading text-sm font-semibold">{t("fin.controls.recurring")} · {d.recurring_vendors.length}</h3>
        {d.recurring_vendors.length === 0 ? <p className="text-xs text-brand-grey">{t("fin.controls.none")}</p> : (
          <div className="mt-1 grid gap-x-6 text-xs md:grid-cols-2">
            {d.recurring_vendors.slice(0, 12).map((r) => (
              <div key={`${r.company}-${r.supplier}`} className="flex items-center justify-between gap-2 border-t border-brand-grey-light/60 py-1">
                <span><CompanyLogo company={r.company} size={12} className="mr-1 align-middle" />{r.supplier} <span className="text-brand-grey">· {r.months} {t("fin.controls.monthsBilled")}</span></span>
                <span className="whitespace-nowrap tabular-nums">{mcdf(r.typical_amount, lng, 1)} <span className="text-brand-grey">· {mcdf(r.annualised, lng, 0)} {t("fin.controls.annualised")}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function CoaSection({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const mutation = useFinanceMutation();
  const [choice, setChoice] = useState<Record<string, string>>({});
  const cov = d.coa.coverage;
  const withBalance = d.coa.unmapped.filter((u) => Math.abs(u.balance) > 0.5);
  const zero = d.coa.unmapped.length - withBalance.length;
  const groupOptions = d.coa.group_accounts.map((g) => ({ value: g.name, label: `${g.name} · ${lng.startsWith("fr") && g.account_name_fr ? g.account_name_fr : g.account_name}` }));
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-brand-grey-light px-4 py-3">
        <h2 className="font-heading text-base font-semibold">{t("fin.coa.title")}</h2>
        <p className="text-xs text-brand-grey">{t("fin.coa.hint")}</p>
      </div>
      {cov && (
        <div className="grid grid-cols-3 gap-3 px-4 py-3 text-center">
          <div><div className={`font-heading text-2xl font-semibold ${cov.rate_pct >= 95 ? "text-status-green" : "text-status-amber"}`}>{fmtNumber(cov.rate_pct, lng, 1)} %</div><div className="text-[11px] text-brand-grey">{cov.mapped_with_balance}/{cov.with_balance} {t("fin.coa.coverage")}</div></div>
          <div><div className="font-heading text-2xl font-semibold">{fmtNumber(cov.automation_pct, lng, 0)} %</div><div className="text-[11px] text-brand-grey">{t("fin.coa.automation")} · {d.coa.rules ?? "–"} {t("fin.coa.rules")}</div></div>
          <div><div className={`font-heading text-2xl font-semibold ${withBalance.length ? "text-status-amber" : "text-status-green"}`}>{withBalance.length}</div><div className="text-[11px] text-brand-grey">{t("fin.coa.unmapped")} · {mcdf(d.coa.unmapped_balance, lng, 2)}</div></div>
        </div>
      )}
      <div className="border-t border-brand-grey-light px-4 py-3 text-xs">
        {withBalance.length === 0 ? <p className="text-brand-grey">{t("fin.coa.none")}</p> : (
          <table className="w-full">
            <tbody>
              {withBalance.map((u) => (
                <tr key={u.account} className="border-t border-brand-grey-light/60">
                  <td className="py-1.5"><CompanyLogo company={u.company} size={12} className="mr-1 align-middle" />{u.account} <span className="text-brand-grey">{u.account_type || u.root_type}</span></td>
                  <td className="py-1.5 text-right tabular-nums">{fmtNumber(u.balance, lng, 0)}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {u.status === "Proposed" && u.map_name ? (
                      <>
                        <Pill value="Draft" label={`${t("fin.coa.proposed")} → ${u.proposed}`} />
                        {d.permissions.can_map && <><button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "decide_mapping", params: { name: u.map_name, status: "Approved" } })} className={`${btn} ml-1 border-status-green text-status-green`}>{t("fin.coa.approve")}</button><button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "decide_mapping", params: { name: u.map_name, status: "Rejected" } })} className={`${btn} ml-1`}>{t("fin.coa.reject")}</button></>}
                      </>
                    ) : d.permissions.can_map ? (
                      <>
                        <select value={choice[u.account] ?? ""} onChange={(e) => setChoice({ ...choice, [u.account]: e.target.value })} className="rounded border border-brand-grey-light bg-white px-1 py-0.5 text-xs">
                          <option value="">{t("fin.coa.mapTo")}</option>{groupOptions.filter((g) => d.coa.group_accounts.find((x) => x.name === g.value)?.root_type === (u.root_type || "")).map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                        <button disabled={!choice[u.account] || mutation.isPending} onClick={() => mutation.mutate({ method: "set_mapping", params: { account: u.account, group_account: choice[u.account] } })} className={`${btnPrimary} ml-1`}>{t("fin.coa.apply")}</button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-2 flex items-center justify-between text-brand-grey">
          <span>{zero ? `${zero} × 0` : ""}</span>
          <span className="flex gap-2">{d.permissions.can_map && <button disabled={mutation.isPending} onClick={() => mutation.mutate({ method: "apply_rules", params: {} })} className={btn}>{t("fin.coa.applyRules")}</button>}</span>
        </div>
      </div>
    </Card>
  );
}

function CloseSection({ d, lng }: { d: FinanceOverview; lng: string }) {
  const { t } = useTranslation();
  const mutation = useFinanceMutation();
  const p = d.close.progress;
  const byName = new Map(d.close.tasks.map((x) => [x.name, x]));
  const today = new Date().toISOString().slice(0, 10);
  const move = (task: CloseTask, status: string) => mutation.mutate({ method: "update_close_task", params: { name: task.name, status } });
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-brand-grey-light px-4 py-3">
        <h2 className="font-heading text-base font-semibold">{t("fin.close.title")} · {fmtPeriod(d.period, lng)}</h2>
        <p className="text-xs text-brand-grey">{t("fin.close.hint")}</p>
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center gap-3 text-xs">
          <div className="h-2 flex-1 overflow-hidden rounded bg-brand-grey-light"><div className="h-2 bg-status-green" style={{ width: `${p.pct}%` }} /></div>
          <span className="font-semibold">{p.pct} %</span><span className="text-brand-grey">{p.done}/{p.total} {t("fin.close.done")} · {p.signed_off} {t("fin.close.statuses.Signed off").toLowerCase()} · <span className={p.overdue ? "text-status-red" : ""}>{p.overdue} {t("fin.close.overdue")}</span> · {p.blocked} {t("fin.close.blocked")}</span>
        </div>
        <ul className="mt-3 space-y-1 text-xs">
          {d.close.tasks.map((task) => {
            const dep = task.depends_on ? byName.get(task.depends_on) : null;
            const overdue = task.due_date && task.due_date < today && task.status !== "Done" && task.status !== "Signed off";
            const depOpen = dep && dep.status !== "Done" && dep.status !== "Signed off";
            return (
              <li key={task.name} className="flex flex-wrap items-center justify-between gap-2 border-t border-brand-grey-light/60 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{task.task}{task.company && <CompanyLogo company={task.company} size={12} className="ml-1.5 align-middle" />}</div>
                  <div className="text-brand-grey">{task.area} · {task.owner_user?.split("@")[0]} · <span className={overdue ? "font-semibold text-status-red" : ""}>{fmtDate(task.due_date)}</span>{dep && <span className={depOpen ? " text-status-amber" : ""}> · {t("fin.close.dependsOn")} « {dep.task} »</span>}{task.signed_by && <span> · {t("fin.close.signedBy")} {task.signed_by.split("@")[0]} {fmtDate(task.signed_on)}</span>}{task.evidence_name && <> · <span>{t("fin.close.evidence")} {task.evidence_name}</span></>}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Pill value={task.status} label={t(`fin.close.statuses.${task.status}`, task.status)} />
                  {d.permissions.can_sign_off && (
                    <>
                      {(task.status === "Pending" || task.status === "Blocked") && <button disabled={mutation.isPending} onClick={() => move(task, "In progress")} className={btn}>{t("fin.close.start")}</button>}
                      {(task.status === "Pending" || task.status === "In progress") && <button disabled={mutation.isPending || Boolean(depOpen)} title={depOpen ? `${t("fin.close.dependsOn")} ${dep?.task}` : ""} onClick={() => move(task, "Done")} className={`${btn} border-status-green text-status-green`}>{t("fin.close.markDone")}</button>}
                      {task.status === "Done" && <button disabled={mutation.isPending} onClick={() => move(task, "Signed off")} className={btnPrimary}>{t("fin.close.signOff")}</button>}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {mutation.isError && <div className="mt-2 rounded bg-red-50 px-3 py-2 text-xs text-status-red">{mutation.error instanceof Error ? mutation.error.message : String(mutation.error)}</div>}
      </div>
    </Card>
  );
}
